// createAesEmbeddedManifestDataUrl.js
'use strict';

import { addBreadcrumb, captureException } from '@sentry/browser';

import fetch from '../resource/customFetch.js';
import { player as playerDebug } from '../resource/debug.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { unwrapAesKey } from '../resource/responseDecryptionUtil.js';
import { Severity } from '../resource/sentry.js';

const playerDebugLog = playerDebug.extend(
  'log:createAesEmbeddedManifestDataUrl'
);
const playerLog = ({ messages, data, level = Severity.Info }) => {
  addBreadcrumb({
    category: 'player',
    level,
    message: messages.join(' '),
    data,
  });
  playerDebugLog(...messages, data);
};

const uint8ToBase64 = bytes => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const ABSOLUTE_URI_SCHEME_REGEX = /^[a-z][a-z0-9+.-]*:/i;
const absolutizeUri = (uri, baseUrl) => {
  if (!uri || ABSOLUTE_URI_SCHEME_REGEX.test(uri)) return uri;
  try {
    return new URL(uri, baseUrl).href;
  } catch (error) {
    return uri;
  }
};

/**
 * Pick the first variant URI from an HLS master playlist.
 *
 * @param {string} manifestText
 * @return {string} eg. variant-360p.m3u8
 * @throws {Error} `aes_embedded_manifest_master_without_variant` when no variant is found.
 */
const pickFirstVariantUri = manifestText => {
  const variantUri = manifestText
    .split(/\r?\n/)
    .map(line => line.trim())
    .find(line => line && !line.startsWith('#'));

  if (!variantUri) {
    throw new Error('aes_embedded_manifest_master_without_variant');
  }
  return variantUri;
};

// Key endpoint returns { nonce, wrapped_key }, not raw bytes.
const fetchAndUnwrapAesKey = async ({
  keyUrl,
  authHeaders,
  clientId,
  sessionId,
  messageId,
}) => {
  let rewrittenKeyUrl = keyUrl;
  try {
    const urlObj = new URL(keyUrl);
    urlObj.host = new URL(getResourceUrl({ endpoint: '/' }).href).host;
    urlObj.pathname = urlObj.pathname.replace(
      /\/messages\/[^/]+\//,
      `/messages/${messageId}/`
    );
    rewrittenKeyUrl = urlObj.href;
  } catch (error) {
    // fall back to original keyUrl
  }

  playerLog({
    messages: ['aes key url rewritten'],
    data: { originalKeyUrl: keyUrl, rewrittenKeyUrl, messageId },
  });

  try {
    const response = await fetch(rewrittenKeyUrl, {
      method: 'GET',
      headers: authHeaders,
    });
    if (!response.ok) {
      throw new Error(
        `aes_embedded_manifest_key_error [${response.status}] ${rewrittenKeyUrl}`
      );
    }
    const { nonce, wrapped_key: wrappedKey } = await response.json();
    const rawKey = await unwrapAesKey({
      clientId,
      sessionId,
      nonce,
      wrappedKey,
    });
    playerLog({
      messages: ['aes key fetched done'],
      data: { keyUrl: rewrittenKeyUrl, keyByteLength: rawKey?.byteLength },
    });
    return rawKey;
  } catch (error) {
    throw new Error(
      `aes_embedded_manifest_key_error: ${error.message} ${rewrittenKeyUrl}`,
      { cause: error }
    );
  }
};

const rewriteMediaPlaylist = async ({
  manifestText,
  manifestUrl,
  authHeaders,
  clientId,
  sessionId,
  messageId,
}) => {
  const inlineAesKeyLine = async line => {
    const uriMatch = line.match(/URI="([^"]+)"/);
    if (!uriMatch) return line;
    const [, keyUrl] = uriMatch;
    if (keyUrl.startsWith('data:')) return line;

    const rawKey = await fetchAndUnwrapAesKey({
      keyUrl,
      authHeaders,
      clientId,
      sessionId,
      messageId,
    });
    const inlined = `data:text/plain;base64,${uint8ToBase64(new Uint8Array(rawKey))}`;
    return line.replace(/URI="[^"]*"/, `URI="${inlined}"`);
  };

  const absolutizeUriAttrs = line =>
    line.replace(
      /URI="([^"]+)"/g,
      (_, uri) => `URI="${absolutizeUri(uri, manifestUrl)}"`
    );

  const rewriteLine = line => {
    if (line.startsWith('#EXT-X-KEY')) return inlineAesKeyLine(line);
    if (line.startsWith('#')) return absolutizeUriAttrs(line);
    if (line.trim() === '') return line;
    return absolutizeUri(line.trim(), manifestUrl);
  };

  const rewritten = await Promise.all(
    manifestText.split(/\r?\n/).map(rewriteLine)
  );
  return rewritten.join('\n');
};

// `#EXT-X-STREAM-INF` is a master-playlist-only tag; presence means it is a master, not a media playlist.
const MASTER_PLAYLIST_REGEX = /^#EXT-X-STREAM-INF/m;

/**
 * Fetch an HLS media playlist, embed the AES-128 key as a data: URI
 *
 * @param {object} params
 * @param {string} params.manifestUrl - original HLS manifest URL.
 * @param {string} params.clientId
 * @param {string} params.sessionId
 * @param {string} params.token
 * @param {string} params.messageId
 * @return {Promise<string>} data URL of the rewritten manifest.
 */
const createAesEmbeddedManifestDataUrl = async ({
  manifestUrl,
  clientId,
  sessionId,
  token,
  messageId,
}) => {
  const authHeaders = {
    ...getHeaders(),
    ...(clientId && { 'X-Client-ID': clientId }),
    ...(sessionId && { 'X-Session-ID': sessionId }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  try {
    const manifestResponse = await fetch(manifestUrl, { method: 'GET' });
    if (!manifestResponse.ok) {
      throw new Error(
        `aes_embedded_manifest_fetch_error [${manifestResponse.status}] ${manifestUrl}`
      );
    }
    const manifestText = await manifestResponse.text();

    playerLog({
      messages: ['manifestText fetched done'],
      data: { manifestUrl, manifestText },
    });

    // Collapse master to first variant: nested data URLs are flaky in
    // Safari, and backend current ships one quality per asset (trace from .m3u8).
    if (MASTER_PLAYLIST_REGEX.test(manifestText)) {
      const variantUri = pickFirstVariantUri(manifestText);
      const variantUrl = new URL(variantUri, manifestUrl).href;

      playerLog({
        messages: ['collapsed master playlist to first variant done'],
        data: { manifestUrl, variantUrl },
      });
      return await createAesEmbeddedManifestDataUrl({
        manifestUrl: variantUrl,
        clientId,
        sessionId,
        token,
        messageId,
      });
    }

    const rewritten = await rewriteMediaPlaylist({
      manifestText,
      manifestUrl,
      authHeaders,
      clientId,
      sessionId,
      messageId,
    });
    const base64 = uint8ToBase64(new TextEncoder().encode(rewritten));
    return `data:application/vnd.apple.mpegurl;base64,${base64}`;
  } catch (error) {
    playerLog({
      level: Severity.Error,
      messages: ['aes embedded manifest failed'],
      data: { manifestUrl, error: error.message },
    });
    captureException(error, {
      tags: { scope: 'createAesEmbeddedManifestDataUrl' },
      extra: { manifestUrl },
    });
    throw new Error(`aes_embedded_manifest_failed: ${error.message}`, {
      cause: error,
    });
  }
};

export default createAesEmbeddedManifestDataUrl;
